import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Modal, ModalBody, ModalContent, ModalOverlay } from "@chakra-ui/react";
import { Formik } from "formik";
import { Flex, Grid, Heading, Text } from "@chakra-ui/react";
import AppListHeader from "@/features/ui/app-list-header";
import AppButton from "@/features/line-sheet-sets/app-button";
import useIdentity from "@/features/ui/hooks/use-identity";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import { isFile, isPrice, isStoredObject } from "@/features/types";
import { mutateIfMatched } from "@/features/ui/utils/swr-utils";
import { useTransportationDumbTransaction } from "@/features/transportations/hooks/use-transportation-dumb-transaction";
import useUpdateTransportation, {
  UpdateTransportationRequest,
} from "@/features/transportations/hooks/use-update-transportation";
import useGetTransportation, {
  GetTransportationParameter,
  GetTransportationRequest,
} from "@/features/transportations/hooks/use-get-transportation";
import useListAllProformaInvoices, {
  ListAllProformaInvoicesPathParameter,
  ListAllProformaInvoicesRequest,
} from "@/features/invoices/proforma-invoices/hooks/use-list-all-proforma-invoices";
import useListShippingInfos from "@/features/my-account/hooks/use-list-shipping-infos";
import TransportationForm from "@/features/transportations/transportation-form";
import {
  PaymentFormState,
  TransportationPaymentInlineForm,
} from "@/features/transportations/transportation-payment-form-control";
import {
  TransportationFormFields,
} from "@/features/transportations/upload-transportation-page";
import {
  TransportationDetail,
  TransportationStatuses,
} from "@/features/transportations/transportation.type";
import { DateUtils } from "@/features/ui/utils/date-utils";
import useCreateTransportationPayment from "@/features/transportations/hooks/use-create-transportation-payment";
import useUpdateTransportationPayment from "@/features/transportations/hooks/use-update-transportation-payment";
import useStoreObject from "@/features/ui/hooks/use-store-object";

interface Props {
  transportationId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const MODAL_HIDDEN_FIELDS: (keyof TransportationFormFields)[] = [
  "shippedFromList",
  "shippedTo",
  "flightNumber",
  "flightDepartureAirport",
  "flightArrivalAirport",
  "flightEtd",
  "flightAtd",
  "flightAta",
  "finalEta",
  "cost",
  "forwarderInvoiceNumbers",
];

const DEFAULT_PAYMENT_FORM: PaymentFormState = {
  issuedOn: new Date(),
  amount: "",
  isRemitted: false,
  remittedOn: null,
  file: null,
};

export default function EditTransportationModal({
  transportationId,
  isOpen,
  onClose,
  onSuccess,
}: Props) {
  const identity = useIdentity();
  const { tTitle } = useI18nHelper();
  const { error: showError, warning: showWarning } = useAppToasts();
  const { addUpdate } = useTransportationDumbTransaction();
  const { fire: fireUpdateTransportation } = useUpdateTransportation();
  const { fire: fireCreatePayment } = useCreateTransportationPayment();
  const { fire: fireUpdatePayment } = useUpdateTransportationPayment();
  const { fire: fireStoreObject } = useStoreObject();

  const [paymentForm, setPaymentForm] = useState<PaymentFormState>(DEFAULT_PAYMENT_FORM);

  const getKey = useMemo(():
    | { parameter: GetTransportationParameter; request: GetTransportationRequest }
    | undefined => {
    if (identity?.company && transportationId) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
          transportationId,
        },
        request: {},
      };
    }
    return undefined;
  }, [identity, transportationId]);

  const {
    data: transportation,
    error: getError,
  } = useGetTransportation(getKey?.parameter, getKey?.request, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  useEffect(() => {
    const payment = transportation?.transportationPayment;
    if (payment) {
      setPaymentForm({
        issuedOn: payment.issuedOn ? new Date(payment.issuedOn) : new Date(),
        amount: isPrice(payment.amount)
          ? String(payment.amount.value)
          : typeof payment.amount === "number"
          ? String(payment.amount)
          : "",
        isRemitted: payment.isRemitted,
        remittedOn: payment.remittedOn ? new Date(payment.remittedOn) : null,
        file: payment.file
          ? {
              id: payment.file.id,
              name: payment.file.name,
              createdAt: payment.file.created_at
                ? new Date(payment.file.created_at)
                : null,
            }
          : null,
      });
    } else {
      setPaymentForm(DEFAULT_PAYMENT_FORM);
    }
  }, [transportation?.transportationPayment]);

  const listProformaInvoicesKey = useMemo(():
    | { parameter: ListAllProformaInvoicesPathParameter; request: ListAllProformaInvoicesRequest }
    | undefined => {
    if (identity?.company && transportation && transportation.proformaInvoices.length > 0) {
      return {
        parameter: {
          by: identity.company.type,
          companyId: identity.company.id,
        },
        request: {
          id__in: transportation.proformaInvoices.map((p) => p.id),
        },
      };
    }
    return undefined;
  }, [identity, transportation]);

  const { data: proformaInvoices } = useListAllProformaInvoices(
    listProformaInvoicesKey?.parameter,
    listProformaInvoicesKey?.request
  );

  const isStockOrder = useMemo(() => {
    return proformaInvoices?.some((p) => p.lineSheetSetType === "STOCK") ?? false;
  }, [proformaInvoices]);

  const validate = useCallback(
    (values: TransportationFormFields) => {
      const errors: Partial<Record<keyof TransportationFormFields, string>> = {};
      if (!isStockOrder && values.proformaInvoices.length === 0) {
        errors.proformaInvoices = "required";
        showWarning(tTitle("proforma_invoices") + " 연결이 필요합니다.");
      }
      return errors;
    },
    [isStockOrder, showWarning, tTitle]
  );

  const { data: shippingInfos } = useListShippingInfos(
    isOpen && transportation?.shippedTo ? { companyId: transportation.shippedTo.id } : undefined,
    { revalidateOnFocus: false, dedupingInterval: 0 }
  );

  const defaultShippingInfo = useMemo(() => {
    if (!shippingInfos || shippingInfos.length === 0) return undefined;
    return shippingInfos.find((info) => info.isDefault) ?? shippingInfos[0];
  }, [shippingInfos]);

  const initialValues = useMemo((): TransportationFormFields => {
    if (transportation) {
      const fallbackAddress = defaultShippingInfo
        ? defaultShippingInfo.address + (defaultShippingInfo.detailAddress ? ` ${defaultShippingInfo.detailAddress}` : "")
        : undefined;
      let details: TransportationDetail[] = [];
      if (transportation.details.length > 0) {
        const i = TransportationStatuses.findIndex(
          (status) =>
            transportation.details[transportation.details.length - 1].status === status
        );
        details = [
          ...transportation.details.map((detail) => ({ ...detail })),
          ...TransportationStatuses.slice(i + 1).map((status) => ({
            status,
            createdAt: null,
          })),
        ];
      }

      return {
        id: transportation.id,
        name: transportation.name,
        status: transportation.status,
        shippedFromList: transportation.shippedFromList,
        shippedTo: transportation.shippedTo,
        shippedToAddress: transportation.shippedToAddress || fallbackAddress,
        shippedToRecipientName: transportation.shippedToRecipientName ?? defaultShippingInfo?.recipientName,
        shippedToContact: transportation.shippedToContact ?? defaultShippingInfo?.contact,
        forwarderInvoiceNumbers: transportation.forwarderInvoiceNumbers.length > 0
          ? transportation.forwarderInvoiceNumbers
          : [""],
        houseBlNumbers: transportation.houseBlNumbers.length > 0
          ? transportation.houseBlNumbers
          : [""],
        mawbNumber: transportation.mawbNumber || undefined,
        domesticCarrierName: transportation.domesticCarrierName || undefined,
        domesticTrackingNumber:
          transportation.domesticTrackingNumber || undefined,
        flightNumber: transportation.flightNumber || undefined,
        flightEtd: transportation.flightEtd ? DateUtils.toDate(transportation.flightEtd) : undefined,
        flightAtd: transportation.flightAtd ? DateUtils.toDate(transportation.flightAtd) : undefined,
        flightDepartureAirport: transportation.flightDepartureAirport || undefined,
        flightEta: transportation.flightEta ? DateUtils.toDate(transportation.flightEta) : undefined,
        flightAta: transportation.flightAta ? DateUtils.toDate(transportation.flightAta) : undefined,
        flightArrivalAirport: transportation.flightArrivalAirport || undefined,
        finalEta: transportation.finalEta || undefined,
        cost: transportation.cost,
        details,
        files: transportation.files,
        proformaInvoices: proformaInvoices ?? [],
      };
    }
    return {
      details: [],
      shippedFromList: [],
      proformaInvoices: [],
      forwarderInvoiceNumbers: [""],
      houseBlNumbers: [""],
      files: [],
    };
  }, [transportation, proformaInvoices, defaultShippingInfo]);

  const handleSubmit = useCallback(
    async (values: TransportationFormFields) => {
      if (!identity?.company || !values.id) return false;

      const parameter = {
        by: identity.company.type,
        companyId: identity.company.id,
        transportationId: values.id,
      };

      const baseRequest = {
        id: values.id,
        status: values.status!,
        name: values.name || "transportation",
        shippedFromList: values.shippedFromList,
        shippedTo: values.shippedTo!,
        shippedToAddress: values.shippedToAddress || "",
        shippedToRecipientName: values.shippedToRecipientName,
        shippedToContact: values.shippedToContact,
        forwarderInvoiceNumbers: values.forwarderInvoiceNumbers.filter(Boolean),
        mawbNumber: values.mawbNumber || null,
        houseBlNumbers: values.houseBlNumbers.filter(Boolean),
        domesticCarrierName: values.domesticCarrierName || null,
        domesticTrackingNumber: values.domesticTrackingNumber || null,
        flightNumber: values.flightNumber || "",
        flightDepartureAirport: values.flightDepartureAirport || "",
        flightArrivalAirport: values.flightArrivalAirport || "",
        flightEtd: values.flightEtd ? DateUtils.toDateTimeString(values.flightEtd) : ("" as any),
        flightAtd: values.flightAtd ? DateUtils.toDateTimeString(values.flightAtd) : null,
        flightEta: values.flightEta ? DateUtils.toDateTimeString(values.flightEta) : ("" as any),
        flightAta: values.flightAta ? DateUtils.toDateTimeString(values.flightAta) : null,
        finalEta: values.finalEta!,
        cost: values.cost || 0,
        proformaInvoices: values.proformaInvoices.map((p) => ({ id: p.id })),
      };

      const isSmart = values.files.every(isStoredObject);

      const paymentAmount = Number(paymentForm.amount);
      const isAgency = identity.company.type === "AGENCY";
      const hasAmount = !!paymentForm.amount && !isNaN(paymentAmount);
      const hasFile = !!paymentForm.file;
      if (isAgency) {
        if (hasFile && !hasAmount) {
          showWarning(tTitle("amount") + " 을(를) 입력해주세요.");
          return false;
        }
        if (hasAmount && !hasFile) {
          showWarning(tTitle("file") + " 자금청구서 파일 첨부가 필요합니다.");
          return false;
        }
      }
      const hasPayment = isAgency && hasFile && hasAmount;

      try {
        if (isSmart) {
          await fireUpdateTransportation(parameter, {
            ...baseRequest,
            files: values.files.filter(isStoredObject),
          } as UpdateTransportationRequest);
          mutateIfMatched(/\/(agencies|buyers)\/\d+\/transportations/);
        } else {
          await addUpdate(parameter, {
            ...baseRequest,
            files: values.files,
          } as any);
        }

        if (hasPayment) {
          let storedFile: { id: number; name: string } | null = null;
          if (paymentForm.file && isFile(paymentForm.file)) {
            const res = await fireStoreObject({}, { file: paymentForm.file });
            storedFile = { id: res.id, name: res.name };
          } else if (paymentForm.file && isStoredObject(paymentForm.file)) {
            storedFile = { id: (paymentForm.file as any).id, name: (paymentForm.file as any).name };
          }

          const issuedOn = new Date(paymentForm.issuedOn);
          issuedOn.setHours(0, 0, 0, 0);

          const paymentRequest = {
            issuedOn,
            file: storedFile,
            amount: { value: paymentAmount, currency: "KRW" as const },
            isRemitted: paymentForm.isRemitted,
            remittedOn: paymentForm.isRemitted ? paymentForm.remittedOn : null,
          };

          if (transportation?.transportationPayment) {
            await fireUpdatePayment(
              { agencyId: identity.company.id, transportationId, paymentId: transportation.transportationPayment.id },
              paymentRequest
            );
          } else {
            await fireCreatePayment(
              { agencyId: identity.company.id, transportationId },
              paymentRequest
            );
          }
        }

        mutateIfMatched(/\/(agencies|buyers)\/\d+\/transportations/);
        onClose();
        onSuccess?.();
      } catch (e) {
        showError(e);
        return false;
      }

      return true;
    },
    [identity, addUpdate, fireUpdateTransportation, fireCreatePayment, fireUpdatePayment, fireStoreObject, paymentForm, transportation, transportationId, onClose, onSuccess, showError, showWarning, tTitle]
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} closeOnOverlayClick={false}>
      <ModalOverlay />
      <ModalContent borderRadius={"8px"} overflow={"hidden"} maxWidth={"1600px"}>
        <ModalBody padding={"0"}>
          <Formik<TransportationFormFields>
            enableReinitialize={true}
            initialValues={initialValues}
            validateOnChange={false}
            validateOnBlur={false}
            validateOnMount={false}
            validate={validate}
            onSubmit={handleSubmit}
          >
            {(formik) => (
              <Grid
                as={"form"}
                height={"100%"}
                width={"100%"}
                gridTemplateColumns={"1fr"}
                gridTemplateRows={"auto 1fr"}
              >
                <AppListHeader
                  headers={[
                    <Heading key={"header"} as={"h2"} fontSize={"14px"}>
                      {tTitle("transportation")}
                    </Heading>,
                  ]}
                  filters={[]}
                  actions={[
                    <AppButton
                      key={"cancel"}
                      isDisabled={formik.isSubmitting}
                      onClick={onClose}
                    >
                      {tTitle("cancel")}
                    </AppButton>,
                    <AppButton
                      key={"save"}
                      variant={"primary"}
                      isLoading={formik.isSubmitting}
                      isDisabled={formik.isSubmitting}
                      onClick={() => formik.submitForm()}
                    >
                      {tTitle("save")}
                    </AppButton>,
                  ]}
                  showNavigateToBack={false}
                />
                <TransportationForm
                  formik={formik}
                  isEditing={true}
                  hiddenFields={MODAL_HIDDEN_FIELDS}
                  rightFooter={
                    identity?.company?.type === "AGENCY" ? (
                      <Flex flexDirection={"column"} gap={"8px"}>
                        <Text fontSize={"14px"} fontWeight={"600"} color={"#444440"}>
                          {tTitle("transportation_payment")}
                        </Text>
                        <Flex
                          flexDirection={"column"}
                          gap={"12px"}
                          padding={"12px"}
                          border={"1px solid #E0E0DC"}
                          borderRadius={"6px"}
                        >
                          <TransportationPaymentInlineForm
                            value={paymentForm}
                            onChange={setPaymentForm}
                            isDisabled={formik.isSubmitting}
                            onBlNumberParsed={(blNumber) => {
                              const parsed = blNumber.trim();
                              if (!parsed) return;
                              const existing = (formik.values.houseBlNumbers ?? []).filter(
                                (b) => b && b.trim()
                              );
                              if (existing.some((b) => b.trim() === parsed)) {
                                return;
                              }
                              formik.setFieldValue("houseBlNumbers", [...existing, parsed]);
                            }}
                          />
                        </Flex>
                      </Flex>
                    ) : undefined
                  }
                />
              </Grid>
            )}
          </Formik>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
